/**
 * Stage 5E.2 Phase 3 / Phase 5 — shared WorkspaceBrandLogo component.
 *
 * Contract logo-fallback chain (locked):
 *
 *     requested variant
 *  →  primary logo
 *  →  firm display name (text fallback)
 *  →  Ordly logo
 *
 * Mode behaviour (contract §20):
 *
 *   • ``standard_ordly`` (or any non-firm_first context, including
 *     anonymous / pre-auth surfaces where no brand context is
 *     mounted) — renders the original Ordly artwork inline.
 *
 *       variant === 'compact'  →  <OrdlyMark size={size} />
 *       otherwise              →  <OrdlyLogo size={size} />
 *
 *     This guarantees the 19 Company-facing surfaces look
 *     visually identical to today for non-branded Companies and
 *     for the AW admin / auth shells.
 *
 *   • ``firm_first`` — renders the firm's logo variant from the
 *     brand context. The image height equals the requested ``size``
 *     (defaults match the original OrdlyLogo defaults), preserving
 *     layout / sizing across the 19 pages without page-level
 *     changes.
 *
 * Phase 5 swap pattern (mechanical 1-line, per §20):
 *
 *     - import { OrdlyLogo } from "../components/ordly/OrdlyLogo";
 *     + import WorkspaceBrandLogo from "../lib/brand/WorkspaceBrandLogo";
 *
 *     - <OrdlyLogo size={28} wordmarkSize={22} />
 *     + <WorkspaceBrandLogo variant="primary" size={28} />
 *
 * No page-level branding state. No independent fetching. No
 * duplicated fallback chain. Pages always receive the correct
 * artwork by consulting the brand context.
 */
import React from "react";
import { useBrand } from "./BrandProvider";
import { OrdlyLogo, OrdlyMark } from "../../components/ordly/OrdlyLogo";

const VALID_VARIANTS = ["primary", "compact", "light", "dark"];

export default function WorkspaceBrandLogo({
    variant = "primary",
    size,                  // numeric height — matches OrdlyLogo API
    alt,
    className,
    style,
    "data-testid": testId,
}) {
    const { mode, logos, displayName } = useBrand();
    const safeVariant = VALID_VARIANTS.includes(variant) ? variant : "primary";

    // ---- standard_ordly (or no brand context) — visual parity ----
    if (mode !== "firm_first" || !logos) {
        // Use the icon-only OrdlyMark for compact variant requests;
        // otherwise the full Ordly lockup. ``size`` is forwarded
        // verbatim so existing per-page sizing is preserved.
        if (safeVariant === "compact") {
            return (
                <OrdlyMark
                    size={size ?? 32}
                    className={className}
                    data-testid={testId || "workspace-brand-logo-ordly-mark"}
                />
            );
        }
        return (
            <OrdlyLogo
                size={size ?? 36}
                className={className}
                data-testid={testId || "workspace-brand-logo-ordly"}
            />
        );
    }

    // ---- firm_first — walk the fallback chain ----
    const requested = logos[safeVariant];
    const primary = logos.primary;
    const src = requested || primary;

    // Sizing for firm artwork: use ``size`` as the rendered height
    // (consistent with how OrdlyLogo's ``size`` prop behaves) and
    // let width follow the natural aspect ratio. Existing inline
    // ``style`` from the caller wins over our height default via
    // spread order.
    const imgStyle = size
        ? { height: size, width: "auto", ...style }
        : style;

    if (src) {
        return (
            <img
                src={src}
                alt={alt || displayName || "Firm logo"}
                className={className}
                style={imgStyle}
                data-testid={testId || `workspace-brand-logo-${safeVariant}`}
            />
        );
    }

    // No image at all → text fallback using firm display name.
    if (displayName) {
        const textStyle = size
            ? {
                fontSize: Math.max(12, Math.round(size * 0.5)),
                lineHeight: 1,
                fontWeight: 600,
                ...style,
            }
            : style;
        return (
            <span
                className={className}
                style={textStyle}
                data-testid={testId || "workspace-brand-logo-text"}
                aria-label={displayName}
            >
                {displayName}
            </span>
        );
    }

    // Last-resort safe Ordly fallback (mode said firm_first but the
    // payload had neither logos nor displayName — preserve render).
    if (safeVariant === "compact") {
        return (
            <OrdlyMark
                size={size ?? 32}
                className={className}
                data-testid={testId || "workspace-brand-logo-ordly-fallback"}
            />
        );
    }
    return (
        <OrdlyLogo
            size={size ?? 36}
            className={className}
            data-testid={testId || "workspace-brand-logo-ordly-fallback"}
        />
    );
}
